import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usd } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, StatusBadge, TableSkeleton, EmptyState } from "@/components/common/primitives";
import { ReceiptText, FileImage, CheckCircle2, ArrowRight, ZoomIn, RotateCw, X, Search } from "lucide-react";

const STAGES = ["Uploading", "Enhancing", "Reading document", "Matching products", "Ready for review"];
const STATUSES = [
  { k: "all", label: "All" },
  { k: "ready_for_review", label: "Ready" },
  { k: "needs_review", label: "Needs review" },
  { k: "posted", label: "Posted" },
];
const STATUS_TONE = { ready_for_review: "success", needs_review: "warning", posted: "neutral" };
const LINE_TONE = { inventory: "success", personal: "neutral", unresolved: "warning" };
const segActive = "ez-seg-active";
const segIdle = "ez-seg text-ink-secondary";

export default function ReceiptIntelligence() {
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { api.receipts().then((r) => setList(r.data)); }, []);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) { document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden"; }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);
  const openReview = () => api.receiptDetail().then((r) => { setDetail(r.data); setOpen(true); });

  const counts = useMemo(() => {
    const c = { all: list?.length || 0 };
    (list || []).forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [list]);
  const summary = useMemo(() => {
    const l = list || [];
    return { total: l.reduce((s, r) => s + r.total, 0), ready: (counts.ready_for_review || 0), needs: (counts.needs_review || 0) };
  }, [list, counts]);
  const view = useMemo(() => {
    let v = [...(list || [])];
    if (status !== "all") v = v.filter((r) => r.status === status);
    if (q.trim()) { const s = q.toLowerCase(); v = v.filter((r) => r.vendor.toLowerCase().includes(s) || r.number.toLowerCase().includes(s)); }
    return v;
  }, [list, status, q]);

  const th = "px-3 py-2 text-micro font-mono uppercase text-ink-muted font-medium whitespace-nowrap";

  return (
    <div className="space-y-4" data-testid="receipts-page">
      <PageHeader title="Receipt Intelligence"
        description="Capture, enhance, OCR and line-match purchase receipts, then post approved purchases, cost layers and inventory."
        meta={<ProvenanceTag kind="fixture" />}
        actions={<button data-testid="receipt-capture" onClick={() => toast("Capture / upload (demo)")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl bg-emerald-600 text-white text-compact hover:bg-emerald-500 ez-glow-emerald ez-press"><FileImage className="h-4 w-4" />Capture</button>} />

      <Panel className="p-3" testid="ocr-stages">
        <div className="flex items-center gap-2 overflow-x-auto ez-scroll">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <span className={cn("inline-flex items-center gap-1 text-micro font-medium px-2 h-6 rounded-ctrl whitespace-nowrap border", i < 4 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-edge-subtle bg-surface-2 text-ink-muted")}>
                {i < 4 && <CheckCircle2 className="h-3 w-3" />}{s}
              </span>
              {i < STAGES.length - 1 && <ArrowRight className="h-3 w-3 text-ink-muted shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </Panel>

      {!list ? <Panel className="p-4"><TableSkeleton rows={4} cols={6} /></Panel> : (
        <>
          <Panel className="px-1 py-1" testid="receipts-ribbon">
            <div className="flex flex-wrap items-stretch divide-x divide-edge-subtle">
              {[["Receipts", String(list.length), null], ["Ready to post", String(summary.ready), "text-emerald-600"], ["Needs review", String(summary.needs), summary.needs ? "text-amber-600" : "text-ink-primary"], ["Captured value", usd(summary.total), null]].map(([label, val, clr]) => (
                <div key={label} className="px-4 py-2 min-w-[130px]">
                  <div className="text-micro uppercase tracking-wide text-ink-muted font-mono">{label}</div>
                  <div className={cn("text-metricsm font-mono tabnum mt-0.5", clr || "text-ink-primary")}>{val}</div>
                </div>
              ))}
            </div>
          </Panel>

          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll" role="tablist" aria-label="Receipt status">
            {STATUSES.map((s) => (
              <button key={s.k} data-testid={`receipt-status-${s.k}`} aria-selected={status === s.k} onClick={() => setStatus(s.k)}
                className={cn("px-2.5 h-8 text-micro font-mono transition-colors", status === s.k ? segActive : segIdle)}>
                {s.label}<span className="ml-1.5 opacity-60">{counts[s.k] || 0}</span>
              </button>
            ))}
          </div>

          <Panel className="p-0 overflow-hidden" testid="receipts-surface">
            <div className="flex items-center gap-2 p-3 border-b border-edge-subtle">
              <div className="flex items-center gap-1.5 h-8 px-2 rounded-ctrl border border-edge-subtle bg-surface-0">
                <Search className="h-3.5 w-3.5 text-ink-muted" />
                <input data-testid="receipt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or number" className="bg-transparent text-compact focus:outline-none w-44" />
              </div>
              <span className="ml-auto text-micro font-mono text-ink-muted">{view.length} of {list.length}</span>
            </div>
            {view.length === 0 ? (
              <EmptyState testid="receipts-empty" icon={ReceiptText} title="No receipts match" hint="Try a different status or search term." />
            ) : (
              <div className="overflow-x-auto ez-scroll"><table className="w-full text-compact">
                <thead>
                  <tr className="bg-surface-2 text-left border-b border-edge-subtle">
                    <th className={th}>Vendor</th><th className={th}>Date</th><th className={th}>Number</th>
                    <th className={cn(th, "text-right")}>Total</th><th className={cn(th, "text-right")}>Lines</th>
                    <th className={cn(th, "text-right")}>Confidence</th><th className={th}>Status</th><th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {view.map((r) => (
                    <tr key={r.id} data-testid={`receipt-row-${r.id}`} className="border-b border-edge-subtle last:border-0 hover:bg-surface-1 h-12">
                      <td className="px-3 text-ink-primary font-medium"><span className="inline-flex items-center gap-2"><ReceiptText className="h-4 w-4 text-ink-muted" />{r.vendor}</span></td>
                      <td className="px-3 font-mono tabnum text-ink-secondary">{r.date}</td>
                      <td className="px-3 ident text-ink-secondary">{r.number}</td>
                      <td className="px-3 text-right font-mono tabnum">{usd(r.total)}</td>
                      <td className="px-3 text-right font-mono tabnum text-ink-secondary">{r.lines}</td>
                      <td className={cn("px-3 text-right font-mono tabnum", r.confidence < 0.75 ? "text-amber-600" : "text-emerald-600")}>{Math.round(r.confidence * 100)}%</td>
                      <td className="px-3"><StatusBadge tone={STATUS_TONE[r.status]}>{r.status.replace(/_/g, " ")}</StatusBadge></td>
                      <td className="px-3"><button onClick={openReview} data-testid={`review-${r.id}`} className="inline-flex items-center h-7 px-2.5 rounded-ctrl border border-emerald-600 text-emerald-600 text-micro font-semibold bg-white hover:bg-emerald-50">Review</button></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Panel>
        </>
      )}

      {open && detail && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex" onClick={() => setOpen(false)} data-testid="receipt-review">
          <div className="ml-auto h-full w-full max-w-5xl bg-surface-1 shadow-float flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 flex items-center justify-between px-4 border-b border-edge-subtle bg-surface-0">
              <div className="font-medium text-ink-primary">{detail.vendor} · <span className="ident">{detail.number}</span></div>
              <button onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-ctrl hover:bg-surface-2"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 grid md:grid-cols-2 overflow-y-auto md:overflow-hidden">
              <div className="border-b md:border-b-0 md:border-r border-edge-subtle bg-navy-950 p-4 flex flex-col min-h-[220px] md:min-h-0">
                <div className="flex items-center gap-2 mb-2">
                  <button className="h-8 w-8 grid place-items-center rounded-ctrl bg-navy-800 text-ink-inverse hover:bg-navy-700"><ZoomIn className="h-4 w-4" /></button>
                  <button className="h-8 w-8 grid place-items-center rounded-ctrl bg-navy-800 text-ink-inverse hover:bg-navy-700"><RotateCw className="h-4 w-4" /></button>
                  <span className="text-micro font-mono text-ink-inverse/50">immutable original · content-hash locked</span>
                </div>
                <div className="flex-1 rounded-panel bg-surface-0 grid place-items-center text-ink-muted">
                  <div className="text-center"><FileImage className="h-12 w-12 mx-auto mb-2" /><span className="text-compact">Receipt image (demo)<br />bounding-box overlays enabled</span></div>
                </div>
              </div>
              <div className="p-4 overflow-y-auto ez-scroll">
                <div className="grid grid-cols-2 gap-2 mb-3 text-compact">
                  {[["Subtotal", detail.subtotal], ["Discount", detail.discount], ["Tax", detail.tax], ["Shipping", detail.shipping], ["Total", detail.total], ["Computed", detail.computed_total]].map(([l, v]) => (
                    <div key={l} className="flex justify-between rounded-ctrl border border-edge-subtle px-2.5 py-1.5"><span className="text-ink-muted">{l}</span><span className="font-mono tabnum">{usd(v)}</span></div>
                  ))}
                  <div className="col-span-2 flex justify-between rounded-ctrl border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1.5"><span>Reconciliation difference</span><span className="font-mono tabnum">{usd(detail.difference)}</span></div>
                </div>
                <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Line items</div>
                {detail.lines.map((ln, i) => (
                  <div key={i} className={cn("rounded-ctrl border p-2.5 mb-2 text-compact", ln.confidence < 0.7 ? "border-amber-300 bg-amber-50/50" : "border-edge-subtle")}>
                    <div className="flex justify-between"><span className="text-ink-primary">{ln.desc}</span><span className="font-mono tabnum">{usd(ln.ext)}</span></div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-micro font-mono text-ink-muted mt-1.5">
                      <span>qty {ln.qty}</span><span>@ {usd(ln.unit)}</span>{ln.upc && <span className="ident">UPC {ln.upc}</span>}
                      <span className={ln.confidence < 0.7 ? "text-amber-600" : "text-emerald-600"}>{Math.round(ln.confidence * 100)}%</span>
                      <StatusBadge tone={LINE_TONE[ln.class]} dot={false}>{ln.match ? `→ ${ln.match}` : ln.class}</StatusBadge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="min-h-16 flex flex-wrap items-center justify-end gap-2 px-4 py-2 border-t border-edge-subtle bg-surface-0">
              <button onClick={() => toast("Draft saved (demo)")} className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact text-ink-secondary hover:bg-surface-2">Save draft</button>
              <button onClick={() => toast.error("Document rejected (demo)")} className="h-9 px-3 rounded-ctrl border border-red-600/40 text-red-600 text-compact hover:bg-red-50">Reject</button>
              <button onClick={() => { toast.success("Inventory posted atomically (demo)"); setOpen(false); }} className="h-9 px-4 rounded-ctrl bg-emerald-600 text-white text-compact hover:bg-emerald-500">Approve &amp; post inventory</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
